import { Badge, Box, Flex, Icon, ResponsiveValue, Text, Tooltip } from '@chakra-ui/react';
import { FiPlus, FiMessageSquare, FiCheck, FiCheckCircle, FiAlertTriangle, FiDollarSign, FiFlag, FiXCircle, FiClock, FiPackage, FiAlertCircle, FiDownload, FiFileText, FiRefreshCw } from 'react-icons/fi';
import { IconType } from 'react-icons';
import { useNavigate } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import React from 'react';
import { Property } from "csstype";
import { NewOrderStatus, QuotingStatus, ReadyToOrderStatus, OrderedStatus, RekeyingStatus } from '../hooks/useGetOrdersFeed';
import { FaExclamationTriangle } from 'react-icons/fa';

type ActionStatus = NewOrderStatus | QuotingStatus | ReadyToOrderStatus | OrderedStatus | RekeyingStatus;

interface ActionBadgeProps {
    action: ActionStatus;
    source: 'home' | 'dashboard';
    orderId: string;
    total?: number;
    expanded?: boolean;
    exclamation?: string;
    flag?: string;
}

const ActionBadge = ({
    action,
    orderId,
    total,
    source,
    expanded = true,
    exclamation,
    flag
}: ActionBadgeProps) => {
    const navigate = useNavigate();

    // Status labels for display
    const statusLabels = useMemo(() => ({
        // New order statuses
        'first-quote': 'First Quote',
        'additional-parts': 'Additional Parts',

        // Quoting statuses
        'in-quoting': 'Quoting',
        'extended-quoting': 'Extended',

        // Ready to order statuses
        'confirm-selections': 'Confirm Selections',
        'below-margin-threshold': 'Low Margin',
        'check-flags': 'Check Flags',
        'parts-not-quoted': 'Parts Not Quoted',
        'potential-total': 'Potential Total',
        'quotes-only': 'Quotes Only',
        'auto-ordered-stopped': 'Auto-Ordered Stopped',

        // Ordered statuses
        'partially-ordered': 'Partially Ordered',
        'all-ordered': 'Fully Ordered',
        'rejected': 'Order Rejected',
        'confirmed': 'Confirmed',
        'not-confirmed': 'Not Confirmed',
        'needs-export': 'Needs Export',
        'needs-rekey': 'Needs Re-key'
    } as Record<ActionStatus, string>), []);

    const statusIcons = useMemo(() => ({
        // New order
        'first-quote': FiPlus,
        'additional-parts': FiPlus,

        // Quoting
        'in-quoting': FiMessageSquare,
        'extended-quoting': FiClock,

        // Ready to order
        'confirm-selections': FiCheck,
        'below-margin-threshold': FiDollarSign,
        'check-flags': FiFlag,
        'parts-not-quoted': FiAlertTriangle,
        'potential-total': FaExclamationTriangle,
        'quotes-only': FiFileText,
        'auto-ordered-stopped': FiXCircle,

        // Ordered
        'partially-ordered': FiPackage,
        'all-ordered': FiCheckCircle,
        'rejected': FiXCircle,
        'confirmed': FiCheckCircle,
        'not-confirmed': FiAlertCircle,
        'needs-export': FiDownload,
        'needs-rekey': FiRefreshCw
    } as Record<ActionStatus, IconType>), []);

    const statusColors = useMemo(() => ({
        // New order
        'first-quote': 'blue.200',
        'additional-parts': 'blue.50',

        // Quoting
        'in-quoting': 'gray.200',
        'extended-quoting': 'orange.200',

        // Ready to order
        'confirm-selections': 'green.100',
        'below-margin-threshold': 'red.200',
        'check-flags': 'orange.200',
        'parts-not-quoted': 'yellow.200',
        'potential-total': 'yellow.100',
        'quotes-only': 'teal.100',
        'auto-ordered-stopped': 'red.100',

        // Ordered
        'partially-ordered': 'blue.100',
        'all-ordered': 'green.100',
        'rejected': 'grey.100',
        'confirmed': 'purple.100',
        'not-confirmed': 'yellow.100',
        'needs-export': 'cyan.100',
        'needs-rekey': 'purple.100'
    } as Record<ActionStatus, ResponsiveValue<Property.Color>>), []);

    const statusColorsDark = useMemo(() => ({
        'first-quote': 'blue.500',
        'additional-parts': 'blue.400',
        'in-quoting': 'gray.500',
        'extended-quoting': 'orange.500',
        'confirm-selections': 'green.500',
        'below-margin-threshold': 'red.500',
        'check-flags': 'orange.500',
        'parts-not-quoted': 'yellow.500',
        'potential-total': 'yellow.500',
        'quotes-only': 'teal.500',
        'auto-ordered-stopped': 'red.500',
        'partially-ordered': 'blue.500',
        'all-ordered': 'green.500',
        'rejected': 'gray.500',
        'confirmed': 'purple.500',
        'not-confirmed': 'yellow.500',
        'needs-export': 'cyan.500',
        'needs-rekey': 'purple.500'
    } as Record<ActionStatus, string>), []);

    const handleBadgeClick = useCallback((action: ActionStatus, e: React.MouseEvent) => {
        e.stopPropagation();
        // Map action statuses to the appropriate order detail tab
        let initialStatus: 'newParts' | 'quoting' | 'selection' | 'ordered' | 'rekey' = 'newParts';

        // New order statuses
        if (action === 'first-quote' || action === 'additional-parts') {
            initialStatus = 'newParts';
        }
        // Quoting statuses
        else if (action === 'in-quoting' || action === 'extended-quoting') {
            initialStatus = 'quoting';
        }
        // Ready to order statuses
        else if (
            action === 'confirm-selections' ||
            action === 'below-margin-threshold' ||
            action === 'check-flags' ||
            action === 'parts-not-quoted' ||
            action === 'potential-total' ||
            action === 'auto-ordered-stopped'
        ) {
            initialStatus = 'selection';
        }
        // Ordered statuses
        else if (
            action === 'partially-ordered' ||
            action === 'all-ordered' ||
            action === 'rejected' ||
            action === 'confirmed' ||
            action === 'not-confirmed'
        ) {
            initialStatus = 'ordered';
        }
        // Rekeying status
        else if (action === 'needs-export' || action === 'needs-rekey') {
            initialStatus = 'rekey';
        }

        navigate(`/order/${orderId}`, { state: { source: source, initialStatus } });
    }, [navigate, orderId, source]);

    const tooltipLabel = useMemo(() => {
        if (!!exclamation) return exclamation;
        if (!!flag && !exclamation) return flag;
        if (!expanded) return statusLabels[action] ?? action;
        return undefined;
    }, [flag, exclamation, expanded, statusLabels, action]);

    const badgeContent = (
        <Badge
            position="relative"
            overflow="visible"
            bg={statusColors[action] || 'gray.500'}
            color="gray.700"
            size="sm"
            py={1}
            px={2}
            fontSize="xs"
            fontWeight="semibold"
            borderRadius="lg"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            onClick={(e) => handleBadgeClick(action, e)}
            _hover={{ opacity: 0.8 }}
            gap={1}
            whiteSpace="nowrap"
            flex={expanded ? "none" : 1}
            minH="28px"
        >
            <Icon as={statusIcons[action] as React.ElementType} w={3} h={3} />
            {expanded && (statusLabels[action] ?? action)}
            {!!total && (
                <Flex
                    ml={expanded ? 1 : 0}
                    bg={statusColorsDark[action] || 'gray.700'}
                    borderRadius="lg"
                    h="18px"
                    minW="18px"
                    px="5px"
                    align="center"
                    justify="center"
                    flexShrink={0}
                >
                    <Text
                        fontSize="9px"
                        fontWeight="bold"
                        lineHeight="1"
                        color="white"
                    >
                        {total}
                    </Text>
                </Flex>
            )}
            {!!exclamation ? (
                <Box
                    position="absolute"
                    top="-6px"
                    right="-6px"
                    bg="red.400"
                    color="white"
                    borderRadius="full"
                    w="16px"
                    h="16px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="xs"
                    zIndex={1}
                >
                    <Icon as={FiAlertCircle as React.ElementType} w="12px" h="12px" />
                </Box>
            ) : !!flag && !exclamation ? (
                <Box
                    position="absolute"
                    top="-6px"
                    right="-6px"
                    bg="orange.400"
                    color="white"
                    borderRadius="full"
                    w="16px"
                    h="16px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="xs"
                    zIndex={1}
                >
                    <Icon as={FiFlag as React.ElementType} w="12px" h="12px" />
                </Box>
            ) : null}
        </Badge>
    );

    if (tooltipLabel) {
        return (
            <Tooltip
                label={tooltipLabel}
                hasArrow
                placement="top"
            >
                {badgeContent}
            </Tooltip>
        );
    }

    return badgeContent;
};

export default ActionBadge;